import { PER_PAGE } from "@/constant/constants";
import { useCallback, useMemo, useRef } from "react";

import { CONFIRMATION_MODAL_TYPES } from "@/Modals/NewConfirmationModal/constants";
import BaseImportModal from "@/Modals/WebDataManagement/MasterData/BaseImport/BaseImportModal";
import { useGet } from "@/apiHooks/HttpFunctions";
import DataTable from "@/components/NewTable";
import { useModalConfirmation } from "@/context/ModalConfirmationContext";
import useUrlParams from "@/hooks/useUrlParams";
import Actions from "./Actions";

export default function BaseTable({
  label,
  myTicketURL,
  apiURL,
  columns = [],
  enableSortData = false,
  ModalComponent,
  importData,
  enableExport = true,
  isFullPageTable = true,
  enablePagination = true,
  additionalFilters = [],
  height = "h-[75vh]",
}) {
  // Hooks
  const params = useUrlParams();
  const { openModalConfirmation } = useModalConfirmation();

  // Refs
  const modalRef = useRef(null);
  const modalImportRef = useRef(null);

  // Derived States
  const filterData = useMemo(() => {
    return [...additionalFilters].filter(Boolean);
  }, [additionalFilters]);

  // Callbacks
  const handleModalAction = useCallback(
    (type, data) => {
      openModalConfirmation({
        type,
        title: data?.name,
        id: data?.id,
        apiURL,
      });
    },
    [openModalConfirmation, apiURL]
  );

  const handleOnClickImport = useCallback(
    () => modalImportRef.current?.open(),
    []
  );

  // Data Fetching
  const { data, isFetching, error, refetch } = useGet({
    apiURL: myTicketURL || apiURL,
    params: {
      perPage: params.perPage || PER_PAGE,
      ...params,
    },
  });

  // Merged Columns
  const mergedColumns = useMemo(() => {
    const baseProps = {
      accessorKey: "actions",
      header: "Actions",
      enableResizing: false,
      enableSorting: false,
      enablePinning: true,
    };
    const actionsColumn = {
      ...baseProps,
      cell: ({ row }) => (
        <Actions
          row={row}
          handleMarkUnread={(data) =>
            handleModalAction(CONFIRMATION_MODAL_TYPES.MARK_UNREAD, data)
          }
          handleReopenTicket={(data) =>
            handleModalAction(CONFIRMATION_MODAL_TYPES.REOPEN_TICKET, data)
          }
          handleCloseTicket={(data) =>
            handleModalAction(CONFIRMATION_MODAL_TYPES.CLOSE_TICKET, data)
          }
        />
      ),
    };

    return [actionsColumn, ...columns];
  }, [columns, handleModalAction]);

  // Render
  if (error) {
    return (
      <div>
        <p>Error loading data...</p>
        <button onClick={refetch}>Retry</button>
      </div>
    );
  }

  return (
    <>
      <DataTable
        data={data}
        height={height}
        columns={mergedColumns}
        isLoading={isFetching}
        searchKey="filter[keyword]"
        exportData={
          enableExport && {
            api: myTicketURL || apiURL,
            label: `${label} List`,
          }
        }
        importData={
          importData && {
            onClick: handleOnClickImport,
          }
        }
        sortData={
          enableSortData && {
            api: myTicketURL || apiURL,
          }
        }
        filterData={filterData}
        isFullPageTable={isFullPageTable}
        enablePagination={enablePagination}
      />

      {ModalComponent && <ModalComponent ref={modalRef} />}
      {importData && (
        <BaseImportModal
          ref={modalImportRef}
          importData={importData}
          apiURL={myTicketURL || apiURL}
        />
      )}
    </>
  );
}
